/**
 * B-52.1 (Oct 2026) — social media links in club News.
 *
 * Clubs post on Instagram / Facebook / X / TikTok / YouTube and paste the link
 * into a Marakin news post. The hub shows a tidy card under the text for each
 * one ("View on Instagram · instagram.com"), drawn from the post's own `body`
 * text only: no embeds, no third-party scripts, no fetched previews (those
 * would set cookies and need a CSP change, and club posts often carry photos
 * of children). The API is untouched, so installed apps keep showing the
 * plain link.
 *
 * Pure — no imports, no DOM — so node --test can run it
 * (socialLinks.test.mjs beside this file).
 */

const PLATFORMS = [
  { platform: 'instagram', name: 'Instagram', hosts: ['instagram.com', 'instagr.am'] },
  { platform: 'facebook', name: 'Facebook', hosts: ['facebook.com', 'fb.com', 'fb.watch', 'fb.me'] },
  { platform: 'x', name: 'X', hosts: ['x.com', 'twitter.com'] },
  { platform: 'tiktok', name: 'TikTok', hosts: ['tiktok.com'] },
  { platform: 'youtube', name: 'YouTube', hosts: ['youtube.com', 'youtu.be'] },
];

export const MAX_SOCIAL_CARDS = 3;

// Leading/trailing characters that are punctuation around a link in prose,
// not part of it: "see (instagram.com/club)." → instagram.com/club
const LEAD = /^[(<[{"'“‘«]+/;
const TRAIL = /[)>\]}"'”’».,!?;:…]+$/;

/** The platform a hostname belongs to (the host itself or any subdomain), or null. */
const platformFor = (hostname) => {
  const h = String(hostname || '').toLowerCase().replace(/\.$/, '');
  return PLATFORMS.find(p => p.hosts.some(d => h === d || h.endsWith(`.${d}`))) || null;
};

// Link-out wrappers the platforms put around outside URLs. (t.co, X's
// wrapper, isn't one of the platform hosts above, so it never gets a card.)
const WRAPPER_HOST = /^(l|lm)\.(facebook|instagram)\.com$/;
const WRAPPER_PATH = /^\/(l\.php|redirect|linkshim)(\/|$)/i;
const isWrapper = (url) => WRAPPER_HOST.test(url.hostname.toLowerCase()) || WRAPPER_PATH.test(url.pathname);

/** One whitespace-separated word → a social link, or null. */
export function parseSocialLink(word) {
  let t = String(word || '').replace(LEAD, '').replace(TRAIL, '');
  if (!t || t.length > 2048) return null;
  // Scheme-less "instagram.com/club" / "www.facebook.com/…" — common when
  // typed on a phone. Anything else without a scheme isn't a link.
  if (!/^https?:\/\//i.test(t)) {
    if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return null; // mailto:, javascript:, …
    t = `https://${t}`;
  }
  let url;
  try { url = new URL(t); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password) return null;
  const p = platformFor(url.hostname);
  if (!p) return null;
  // A bare "x.com" or "youtube.com" is the platform, not a post or profile.
  if (url.pathname.replace(/\/+$/, '') === '') return null;
  // Redirect wrappers (l.facebook.com, facebook.com/l.php, youtube.com/redirect…)
  // lead somewhere else entirely: leave them as plain links, no branded card.
  if (isWrapper(url)) return null;
  url.protocol = 'https:'; // all five platforms serve https; never open http
  return {
    platform: p.platform,
    name: p.name,
    label: `View on ${p.name}`,
    host: url.hostname.toLowerCase().replace(/^(www|m|mobile|web)\./, ''),
    href: url.href,
  };
}

/** Every social link in a post body, in order, de-duplicated, at most MAX_SOCIAL_CARDS. */
export function findSocialLinks(body) {
  const out = [];
  const seen = new Set();
  for (const word of String(body || '').split(/\s+/)) {
    if (out.length >= MAX_SOCIAL_CARDS) break;
    if (!word || !/[./]/.test(word)) continue;
    const link = parseSocialLink(word);
    if (!link || seen.has(link.href)) continue;
    seen.add(link.href);
    out.push(link);
  }
  return out;
}

// Brackets and quotes that wrap a link in prose: "(instagram.com/p/1)".
const PAIRS = { '(': ')', '<': '>', '[': ']', '{': '}', '"': '"', "'": "'", '“': '”', '‘': '’', '«': '»' };

/**
 * WT-28 (B-52): the post text to show above its link cards. A link that gets
 * a card is not printed again as a raw URL; everything else stays, including
 * links with no card (other sites, past the card limit). A bracket or quote
 * wrapped round the link goes with it, other punctuation stays:
 *   "Photos (https://instagram.com/p/1), report to follow." → "Photos, report to follow."
 * A line that was only the link goes. Text with no carded links comes back
 * unchanged.
 */
export function bodyWithoutCardLinks(body) {
  const text = String(body ?? '');
  const carded = new Set(findSocialLinks(text).map(l => l.href));
  if (!carded.size) return text;
  const out = [];
  for (const line of text.split('\n')) {
    let changed = false;
    const next = line.replace(/([ \t]*)(\S+)/g, (whole, space, word) => {
      if (!/[./]/.test(word)) return whole;
      const link = parseSocialLink(word);
      if (!link || !carded.has(link.href)) return whole;
      changed = true;
      let lead = (word.match(LEAD) || [''])[0];
      let trail = (word.replace(LEAD, '').match(TRAIL) || [''])[0];
      while (lead && trail && PAIRS[lead[lead.length - 1]] === trail[0]) {
        lead = lead.slice(0, -1);
        trail = trail.slice(1);
      }
      const rest = lead + trail;
      if (!rest) return '';
      // "Instagram (link)," → "Instagram," — no space before the comma.
      return /^[).,!?;:…\]}>»”’'"]/.test(rest) ? rest : space + rest;
    });
    if (!changed) { out.push(line); continue; }
    // "match report: <link>." → "match report:" (not ":.").
    const tidy = next.replace(/([:;,])[.,;:!?…]+(?=\s|$)/g, '$1').replace(/[ \t]{2,}/g, ' ').trim();
    // A line left with only punctuation ("…:", ", ,") goes too.
    if (tidy && /[\p{L}\p{N}]/u.test(tidy)) out.push(tidy);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
